export const ESTIMATE_TAX_TYPE_OPTIONS = Object.freeze([
  { value: "none", label: "None" },
  { value: "gst", label: "GST" },
  { value: "qst", label: "QST" },
  { value: "hst", label: "HST" },
  { value: "custom", label: "Custom" },
]);

export const ESTIMATE_TAX_TYPE_VALUES = Object.freeze(
  ESTIMATE_TAX_TYPE_OPTIONS.map((option) => option.value),
);

export const DEFAULT_ESTIMATE_TAX_LINE = Object.freeze({
  type: "none",
  rate: "",
  customName: "",
});

/** Locked rates for standard tax types — not user-editable. */
export const ESTIMATE_TAX_DEFAULT_RATES = Object.freeze({
  none: 0,
  gst: 5,
  qst: 9.975,
  hst: 13,
});

const TAX_LABELS = Object.freeze({
  gst: "GST",
  qst: "QST",
  hst: "HST",
});

function roundCurrency(amount) {
  return Math.round((Number(amount) + Number.EPSILON) * 100) / 100;
}

export function resolveTaxType(type) {
  return ESTIMATE_TAX_TYPE_VALUES.includes(type) ? type : "none";
}

export function getTaxLineLabel(line) {
  const type = resolveTaxType(line?.type);
  if (type === "custom") {
    const name = String(line?.customName ?? line?.name ?? "").trim();
    return name || "Custom Tax";
  }
  return TAX_LABELS[type] || "Tax";
}

export function formatTaxRateForInput(rate) {
  if (rate == null || Number(rate) === 0) return "";
  return String(rate);
}

export function isStandardTaxType(type) {
  const resolved = resolveTaxType(type);
  return resolved === "none" || resolved === "gst" || resolved === "qst" || resolved === "hst";
}

/** Display string for locked standard tax rates. */
export function formatStandardTaxRateDisplay(type) {
  const resolved = resolveTaxType(type);
  if (resolved === "custom") return "";

  if (resolved === "qst") return "9.975";
  if (resolved === "none") return "0.00";

  const rate = ESTIMATE_TAX_DEFAULT_RATES[resolved];
  return rate != null ? Number(rate).toFixed(2) : "0.00";
}

export function resolveTaxLineRate(line) {
  const type = resolveTaxType(line?.type);
  if (type === "none") return 0;
  if (type === "custom") {
    return Number(String(line?.rate ?? "").trim() || 0);
  }
  return ESTIMATE_TAX_DEFAULT_RATES[type] ?? 0;
}

/** Apply default rate when a standard tax type is selected. Custom requires manual entry. */
export function applyTaxTypeSelection(currentLine, nextType) {
  const type = resolveTaxType(nextType);

  if (type === "none") {
    return {
      type: "none",
      rate: "",
      customName: "",
    };
  }

  if (type === "custom") {
    return {
      type: "custom",
      rate: "",
      customName: currentLine?.customName || "",
    };
  }

  const defaultRate = ESTIMATE_TAX_DEFAULT_RATES[type];
  return {
    type,
    rate: formatTaxRateForInput(defaultRate),
    customName: "",
  };
}

export function parseTaxRateInput(value) {
  const trimmed = String(value ?? "").trim();
  if (!trimmed) return 0;

  const rate = Number(trimmed);
  if (Number.isNaN(rate) || rate < 0 || rate > 100) {
    throw new Error("Enter a valid tax rate between 0 and 100.");
  }

  return rate;
}

export function parseEstimateTaxLineFromTree(estimateTree, slot) {
  const prefix = `tax_line_${slot}`;
  const type = resolveTaxType(estimateTree?.[`${prefix}_type`]);
  const storedRate = estimateTree?.[`${prefix}_rate`];
  let rate =
    storedRate == null || Number(storedRate) === 0
      ? ""
      : String(storedRate);

  if (type !== "none" && type !== "custom") {
    rate = formatTaxRateForInput(ESTIMATE_TAX_DEFAULT_RATES[type]);
  }

  const customName = estimateTree?.[`${prefix}_name`] || "";

  return {
    type,
    rate,
    customName,
  };
}

export function parseEstimateTaxLinesFromTree(estimateTree) {
  return [1, 2].map((slot) => parseEstimateTaxLineFromTree(estimateTree, slot));
}

export function taxLineToFormState(line = DEFAULT_ESTIMATE_TAX_LINE) {
  const type = resolveTaxType(line.type);

  if (type === "custom") {
    return {
      type,
      rate:
        line.rate == null || line.rate === "" || Number(line.rate) === 0
          ? ""
          : String(line.rate),
      customName: line.customName || line.name || "",
    };
  }

  if (type !== "none") {
    return {
      type,
      rate: formatTaxRateForInput(ESTIMATE_TAX_DEFAULT_RATES[type]),
      customName: "",
    };
  }

  return {
    type: "none",
    rate: "",
    customName: "",
  };
}

export function normalizeTaxLineForSave(line, lineNumber) {
  const type = resolveTaxType(line?.type);
  const label = `Tax line ${lineNumber}`;

  if (type === "none") {
    return {
      type: "none",
      rate: 0,
      name: null,
    };
  }

  if (type === "gst" || type === "qst" || type === "hst") {
    return {
      type,
      rate: ESTIMATE_TAX_DEFAULT_RATES[type],
      name: null,
    };
  }

  const rate = parseTaxRateInput(line?.rate);
  if (rate === 0) {
    return {
      type: "none",
      rate: 0,
      name: null,
    };
  }

  if (type === "custom") {
    const customName = String(line?.customName ?? "").trim();
    if (!customName) {
      throw new Error(`${label}: enter a tax name for custom tax.`);
    }

    return {
      type: "custom",
      rate,
      name: customName,
    };
  }

  return {
    type: "none",
    rate: 0,
    name: null,
  };
}

export function normalizeEstimateTaxLinesForSave(taxLines = []) {
  const lines = Array.isArray(taxLines) ? taxLines : [];
  const normalized = [1, 2].map((slot) =>
    normalizeTaxLineForSave(lines[slot - 1] || DEFAULT_ESTIMATE_TAX_LINE, slot),
  );

  return {
    tax_line_1_type: normalized[0].type,
    tax_line_1_rate: normalized[0].rate,
    tax_line_1_name: normalized[0].name,
    tax_line_2_type: normalized[1].type,
    tax_line_2_rate: normalized[1].rate,
    tax_line_2_name: normalized[1].name,
  };
}

/**
 * Taxable subtotal is Final Estimate Price when set, otherwise adjusted total.
 */
export function getTaxableSubtotal(financials, { liveFinalEstimatePrice } = {}) {
  const liveTrimmed = String(liveFinalEstimatePrice ?? "").trim();
  if (liveTrimmed) {
    const liveAmount = Number(liveTrimmed);
    if (!Number.isNaN(liveAmount) && liveAmount >= 0) {
      return liveAmount;
    }
  }

  if (financials?.finalEstimatePrice != null) {
    return financials.finalEstimatePrice;
  }

  return Number(financials?.adjustedTotal) || 0;
}

/**
 * Desktop receipt math.
 * Subtotal is the estimate. Waste, travel, and misc always add into that
 * subtotal. Each extra is taxed unless its flag is false.
 * An amount adjustment is a dollar delta. A percent adjustment is a percent
 * of that subtotal. The adjustment is its own line, then one subtotal.
 * A stored final price with no adjustment is shown as the dollar gap so the
 * lines still add up to that price, and that price stays the tax base.
 * Existing rows with a null tax flag stay taxable.
 */
export function computeLivePricing({
  estimateSubtotal = 0,
  waste = 0,
  travel = 0,
  misc = 0,
  wasteTaxable = true,
  travelTaxable = true,
  miscTaxable = true,
  finalPrice = "",
  adjustmentMode = "",
  adjustmentValue = "",
  taxLines = [],
} = {}) {
  const amount = (value) => Number(value) || 0;
  const extras = [
    { amount: amount(waste), taxable: wasteTaxable !== false },
    { amount: amount(travel), taxable: travelTaxable !== false },
    { amount: amount(misc), taxable: miscTaxable !== false },
  ];
  const extrasSubtotal = roundCurrency(
    amount(estimateSubtotal) + extras.reduce((sum, extra) => sum + extra.amount, 0),
  );
  const trimmedAdjustment = String(adjustmentValue ?? "").trim();
  const parsedAdjustment = Number(trimmedAdjustment);
  const hasAdjustment = trimmedAdjustment !== ""
    && Number.isFinite(parsedAdjustment)
    && (adjustmentMode === "amount" || adjustmentMode === "percent");
  const adjustment = hasAdjustment
    ? roundCurrency(
      adjustmentMode === "percent"
        ? extrasSubtotal * (parsedAdjustment / 100)
        : parsedAdjustment,
    )
    : 0;
  const trimmed = String(finalPrice ?? "").trim();
  const finalNum = trimmed === "" ? null : Number(trimmed);
  const hasFinal = !hasAdjustment && finalNum != null && !Number.isNaN(finalNum) && finalNum >= 0;
  const legacyAdjustment = hasFinal ? roundCurrency(finalNum - extrasSubtotal) : 0;
  const appliedAdjustment = hasAdjustment ? adjustment : legacyAdjustment;
  const preTax = roundCurrency(extrasSubtotal + appliedAdjustment);
  const taxableExtras = extras.filter((extra) => extra.taxable).reduce((sum, extra) => sum + extra.amount, 0);
  const taxableBase = hasFinal
    ? preTax
    : roundCurrency(amount(estimateSubtotal) + taxableExtras + appliedAdjustment);
  const taxSummary = computeEstimateTaxSummary(taxableBase, taxLines);
  return {
    beforeTax: extrasSubtotal,
    adjustment: appliedAdjustment,
    preTax,
    taxableBase,
    difference: hasFinal ? legacyAdjustment : null,
    taxSummary: {
      ...taxSummary,
      grandTotal: roundCurrency(preTax + taxSummary.totalTax),
    },
  };
}

/** Before-tax total the estimate, quote, and PDF should agree on. */
export function presentedSubtotal(estimateTree, financials) {
  return computeLivePricing({
    estimateSubtotal: financials?.calculatedEstimateTotal,
    waste: financials?.wasteAdjustment ?? estimateTree?.waste_adjustment,
    travel: financials?.travelAdjustment ?? estimateTree?.travel_adjustment,
    misc: financials?.miscAdjustment ?? estimateTree?.misc_adjustment,
    wasteTaxable: estimateTree?.waste_taxable !== false,
    travelTaxable: estimateTree?.travel_taxable !== false,
    miscTaxable: estimateTree?.misc_taxable !== false,
    adjustmentMode: estimateTree?.subtotal_adjustment_mode,
    adjustmentValue: estimateTree?.subtotal_adjustment_value,
    finalPrice: financials?.finalEstimatePrice ?? estimateTree?.final_estimate_price,
  }).preTax;
}

export function computeEstimateTaxSummary(taxableSubtotal, taxLines = []) {
  const subtotal = roundCurrency(Number(taxableSubtotal) || 0);
  const lines = (Array.isArray(taxLines) ? taxLines : [])
    .map((line) => {
      const type = resolveTaxType(line?.type);
      if (type === "none") return null;

      const rate = resolveTaxLineRate(line);
      if (Number.isNaN(rate) || rate <= 0) return null;

      const amount = roundCurrency((subtotal * rate) / 100);

      return {
        type,
        label: getTaxLineLabel(line),
        rate,
        customName: line?.customName || line?.name || null,
        amount,
      };
    })
    .filter(Boolean);

  const totalTax = roundCurrency(
    lines.reduce((sum, line) => sum + line.amount, 0),
  );

  return {
    subtotal,
    lines,
    totalTax,
    grandTotal: roundCurrency(subtotal + totalTax),
    hasTax: lines.length > 0,
  };
}

export function formatTaxLineLabel(line) {
  const rateLabel =
    line.rate === 9.975
      ? "9.975"
      : Number(line.rate) % 1 === 0
        ? String(line.rate)
        : String(line.rate);
  return `${line.label} (${rateLabel}%)`;
}
